import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  shareCreditTemplateList as fetchShareCreditTemplateListSvc,
  templateCategoryList as fetchTemplateCategoryListSvc,
} from '@/services/tenant/award-service';
import { fetchFinalAwardSummaryForView as fetchSummaryService } from '@/services/tenant/final-award-service';

interface AwardState {
  awardTemplateTypeList: any[];
  shareCreditTemplateList: any[];
  // Non-built-in (custom) templates used only by the "Letter to SGX" dropdown.
  sgxLetterTemplateList: any[];
  // Master template categories; used to resolve the "Share Crediting" categoryId.
  templateCategories: any[];
  finalAwardSummary: any;
  loading: boolean;
  error: string | null;
}

const initialState: AwardState = {
  awardTemplateTypeList: [],
  shareCreditTemplateList: [],
  sgxLetterTemplateList: [],
  templateCategories: [],
  finalAwardSummary: null,
  loading: false,
  error: null,
};

export const fetchAwardTemplateType = createAsyncThunk(
  'award/fetchAwardTemplateType',
  async () => {
    const categories = await fetchTemplateCategoryListSvc();
    const awardCategory = (categories || []).find(
      (c: any) => c?.categoryName === 'Award'
    );
    const categoryId = awardCategory?.id;
    if (!categoryId) return [];

    const response = await fetchShareCreditTemplateListSvc(categoryId);
    return response;
  }
);

export const fetchTemplateCategories = createAsyncThunk(
  'award/fetchTemplateCategories',
  async () => {
    const response = await fetchTemplateCategoryListSvc();
    return response;
  }
);

export const fetchShareCreditTemplateType = createAsyncThunk(
  'award/fetchShareCreditTemplateType',
  async (categoryId: number) => {
    const response = await fetchShareCreditTemplateListSvc(categoryId);
    return response;
  }
);

// Fetches only non-built-in templates (isBuiltIn=false) for the "Letter to SGX"
// dropdown. Kept separate from the Step 4 list so each surface gets its own set.
export const fetchSgxLetterTemplateType = createAsyncThunk(
  'award/fetchSgxLetterTemplateType',
  async (categoryId: number) => {
    const response = await fetchShareCreditTemplateListSvc(categoryId, false);
    return response;
  }
);

export const fetchFinalAwardSummary = createAsyncThunk(
  'award/fetchFinalAwardSummary',
  async (awardNo: string) => {
    const response = await fetchSummaryService(awardNo);
    return response;
  }
);

const awardSlice = createSlice({
  name: 'award',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAwardTemplateType.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAwardTemplateType.fulfilled, (state, action) => {
        state.loading = false;
        state.awardTemplateTypeList = action.payload;
      })
      .addCase(fetchAwardTemplateType.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch award template types';
      })
      .addCase(fetchTemplateCategories.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplateCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.templateCategories = action.payload;
      })
      .addCase(fetchTemplateCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch template categories';
      })
      .addCase(fetchShareCreditTemplateType.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShareCreditTemplateType.fulfilled, (state, action) => {
        state.loading = false;
        state.shareCreditTemplateList = action.payload;
      })
      .addCase(fetchShareCreditTemplateType.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch share credit templates';
      })
      .addCase(fetchSgxLetterTemplateType.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSgxLetterTemplateType.fulfilled, (state, action) => {
        state.loading = false;
        state.sgxLetterTemplateList = action.payload;
      })
      .addCase(fetchSgxLetterTemplateType.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch SGX letter templates';
      })
      .addCase(fetchFinalAwardSummary.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinalAwardSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.finalAwardSummary = action.payload;
      })
      .addCase(fetchFinalAwardSummary.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || 'Failed to fetch final award summary';
      });
  },
});

export default awardSlice.reducer;
